/**
 * @fileoverview V2 Treatment Step — Unified provider form cards.
 *
 * Each provider (imported or manual) uses the same ProviderEntryForm shape.
 * Import pre-fills the cards with AI badges. Manual starts empty.
 * No bifurcated UI — one form, one code path.
 *
 * Audit-tracked: updates `medical_providers` section as found/not_found.
 */
import { Pencil, Plus, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { toTitleCase } from '@/lib/strings';
import {
  useGetCaseIngestionQuery,
  useUpdateCaseIngestionSectionsMutation,
} from '@/services/api/caseIngestionService';
import {
  useDeleteMedicalProviderMutation,
  useGetMedicalProvidersQuery,
  useUpdateMedicalProviderMutation,
} from '@/services/api/treatmentService';
import type { ImportedProvider } from '@/services/types/case-ingestion-assist-types';
import {
  ContactHandleType,
  type MedicalProvider,
  ProviderTreatmentStatus,
  TreatmentCareLevel,
} from '@/services/types/treatment-types';

import { FormCheckbox, FormGroup, FormSection } from '../components/form';
import {
  EMPTY_PROVIDER,
  ProviderEntryForm,
  type ProviderFormData,
} from '../components/ProviderEntryForm';
import { useIngestion } from '../state/IngestionProvider';

type PendingEntry = {
  id: string;
  data: ProviderFormData;
  isImported: boolean;
};

type EditProviderInlineProps = {
  provider: MedicalProvider;
  onSave: (patch: Partial<MedicalProvider>) => Promise<void>;
  onCancel: () => void;
};

function importedToFormData(importedProvider: ImportedProvider): ProviderFormData {
  return {
    name: importedProvider.name,
    type: importedProvider.type ?? '',
    address_line1: importedProvider.address_line1 ?? '',
    address_line2: importedProvider.address_line2 ?? '',
    city: importedProvider.city ?? '',
    state: importedProvider.state ?? '',
    postcode: importedProvider.postcode ?? '',
    phone: importedProvider.phone ?? '',
    fax: importedProvider.fax ?? '',
    treatment_status: matchTreatmentStatus(importedProvider.treatment_status),
    care_level: matchCareLevel(importedProvider.care_level),
    provider_type: matchFacilityType(importedProvider.facility_type),
  };
}

function matchTreatmentStatus(
  rawValue: string | null,
): ProviderTreatmentStatus | '' {
  if (!rawValue) {
    return '';
  }

  const normalizedValue = rawValue.toLowerCase().replace(/[\s_-]+/g, '_');

  return (
    Object.values(ProviderTreatmentStatus).find(
      (status) => status.toLowerCase() === normalizedValue,
    ) ?? ''
  );
}

function matchCareLevel(rawValue: string | null): TreatmentCareLevel | '' {
  if (!rawValue) {
    return '';
  }

  const normalizedValue = rawValue.toLowerCase().replace(/[\s_-]+/g, '_');

  return (
    Object.values(TreatmentCareLevel).find(
      (careLevel) => careLevel.toLowerCase() === normalizedValue,
    ) ?? ''
  );
}

function matchFacilityType(rawValue: string | null): 'hospital' | 'clinic' | '' {
  if (!rawValue) {
    return '';
  }

  const normalizedValue = rawValue.toLowerCase().trim();

  if (normalizedValue === 'hospital' || normalizedValue === 'hospital_system') {
    return 'hospital';
  }

  if (
    normalizedValue === 'clinic' ||
    normalizedValue === 'clinic_independent' ||
    normalizedValue === 'non-hospital' ||
    normalizedValue === 'non_hospital'
  ) {
    return 'clinic';
  }

  return '';
}

function getHandleValue(
  provider: MedicalProvider,
  handleType: ContactHandleType,
): string {
  return (
    provider.handles?.find((handle) => handle.handle_type === handleType)?.value ??
    ''
  );
}

function getDisplayAddress(provider: MedicalProvider): string {
  return (
    getHandleValue(provider, ContactHandleType.MAILING_ADDRESS) ||
    provider.address ||
    ''
  );
}

function getSelectLabel(value: string): string {
  return toTitleCase(value.replace(/_/g, ' '));
}

export const TreatmentStep = (): React.JSX.Element => {
  const { state, dispatch, registerSave, importData } = useIngestion();
  const { matterId, stepIndex } = state;
  const activeMatterId = matterId ?? '';

  const {
    data: savedProviders = [],
    isLoading: isLoadingProviders,
    refetch,
  } = useGetMedicalProvidersQuery(activeMatterId, {
    skip: !activeMatterId,
  });
  const { data: auditRecord } = useGetCaseIngestionQuery(activeMatterId, {
    skip: !activeMatterId,
  });
  const [updateSections] = useUpdateCaseIngestionSectionsMutation();
  const [deleteMedicalProvider, { isLoading: isDeleting }] =
    useDeleteMedicalProviderMutation();
  const [updateMedicalProvider] = useUpdateMedicalProviderMutation();

  const [notFound, setNotFound] = useState(false);
  const [pendingEntries, setPendingEntries] = useState<PendingEntry[]>([]);
  const [editingProviderId, setEditingProviderId] = useState<string | null>(null);
  const hasInitialized = useRef(false);

  const sectionStatus = auditRecord?.sections?.medical_providers ?? null;
  const importedProviders = useMemo<ImportedProvider[]>(() => {
    if (importData?.payload?.treatment?.providers?.presence !== 'present') {
      return [];
    }

    return importData.payload.treatment.providers.value ?? [];
  }, [importData]);

  useEffect(() => {
    if (hasInitialized.current || auditRecord === undefined || isLoadingProviders) {
      return;
    }

    hasInitialized.current = true;
    setNotFound(sectionStatus === 'not_found');

    if (savedProviders.length > 0 || importedProviders.length === 0) {
      return;
    }

    setPendingEntries(
      importedProviders.map((importedProvider, index) => ({
        id: `import-${index}`,
        data: importedToFormData(importedProvider),
        isImported: true,
      })),
    );
  }, [
    auditRecord,
    importedProviders,
    isLoadingProviders,
    savedProviders.length,
    sectionStatus,
  ]);

  const hasPendingEntries = pendingEntries.length > 0;
  const hasSavedProviders = savedProviders.length > 0;
  const hasAnything = hasSavedProviders || hasPendingEntries;

  const handleAddEmpty = useCallback((): void => {
    setPendingEntries((currentEntries) => [
      ...currentEntries,
      {
        id: `manual-${Date.now()}`,
        data: { ...EMPTY_PROVIDER },
        isImported: false,
      },
    ]);
  }, []);

  const handleRemovePending = useCallback((entryId: string): void => {
    setPendingEntries((currentEntries) =>
      currentEntries.filter((entry) => entry.id !== entryId),
    );
  }, []);

  const handleProviderAdded = useCallback(
    (entryId: string): void => {
      setPendingEntries((currentEntries) =>
        currentEntries.filter((entry) => entry.id !== entryId),
      );
      refetch();
    },
    [refetch],
  );

  const handleDeleteSaved = useCallback(
    async (providerId: string): Promise<void> => {
      const isLastSavedProvider = savedProviders.length === 1;

      try {
        await deleteMedicalProvider({
          matterId: activeMatterId,
          providerId,
        }).unwrap();

        if (isLastSavedProvider) {
          await updateSections({
            matterId: activeMatterId,
            data: { sections: { medical_providers: null } },
          }).unwrap();
        }
      } catch {
        toast.error('Failed to remove provider');
      }
    },
    [activeMatterId, deleteMedicalProvider, savedProviders.length, updateSections],
  );

  const saveForm = useCallback(async (): Promise<boolean> => {
    if (hasPendingEntries) {
      toast.error('Please add or remove all pending providers before continuing');
      return false;
    }

    let nextSectionStatus: 'found' | 'not_found' | null = null;
    if (notFound) {
      nextSectionStatus = 'not_found';
    } else if (hasSavedProviders) {
      nextSectionStatus = 'found';
    }

    try {
      if (nextSectionStatus) {
        await updateSections({
          matterId: activeMatterId,
          data: { sections: { medical_providers: nextSectionStatus } },
        }).unwrap();
      }

      const successMessage = hasSavedProviders
        ? `${savedProviders.length} provider(s) saved`
        : 'Step completed';
      toast.success(successMessage);
      return true;
    } catch {
      toast.error('Failed to save step');
      return false;
    }
  }, [
    hasPendingEntries,
    hasSavedProviders,
    activeMatterId,
    notFound,
    savedProviders.length,
    updateSections,
  ]);

  useEffect(() => {
    registerSave(saveForm);

    return () => {
      registerSave(null);
    };
  }, [registerSave, saveForm]);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    const didSave = await saveForm();
    if (!didSave) {
      return;
    }

    dispatch({ type: 'GO_TO_STEP', index: stepIndex + 1 });
  }

  return (
    <form onSubmit={onSubmit} className="space-y-8">
      <FormSection
        title="Medical Treatment"
        description="Add medical providers where the client received treatment"
      >
        <FormGroup>
          {!hasAnything && (
            <FormCheckbox
              label="No providers found"
              description="Check if no medical providers have been identified"
              checked={notFound}
              onChange={(event) => {
                setNotFound((event.target as HTMLInputElement).checked);
              }}
            />
          )}

          {!notFound && (
            <div className="space-y-4">
              {pendingEntries.map((entry, index) => (
                <ProviderEntryForm
                  key={entry.id}
                  data={entry.data}
                  isImported={entry.isImported}
                  index={savedProviders.length + index}
                  matterId={activeMatterId}
                  onAdded={() => handleProviderAdded(entry.id)}
                  onRemove={() => handleRemovePending(entry.id)}
                />
              ))}

              {hasSavedProviders && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-zinc-700">
                    Added Providers ({savedProviders.length})
                  </p>
                  {savedProviders.map((provider) => {
                    if (editingProviderId === provider.id) {
                      return (
                        <EditProviderInline
                          key={provider.id}
                          provider={provider}
                          onSave={async (patch) => {
                            try {
                              await updateMedicalProvider({
                                matterId: activeMatterId,
                                providerId: provider.id,
                                provider: patch,
                              }).unwrap();
                              toast.success('Provider updated');
                              setEditingProviderId(null);
                            } catch {
                              toast.error('Failed to update provider');
                            }
                          }}
                          onCancel={() => setEditingProviderId(null)}
                        />
                      );
                    }

                    const phone = getHandleValue(provider, ContactHandleType.PHONE);
                    const displayAddress = getDisplayAddress(provider);

                    return (
                      <div
                        key={provider.id}
                        className="flex items-start justify-between gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-zinc-900">
                            {provider.name || 'Unnamed provider'}
                          </p>
                          {displayAddress && (
                            <p className="truncate text-xs font-medium text-zinc-500">
                              {displayAddress}
                            </p>
                          )}
                          {phone && (
                            <p className="text-xs font-medium text-zinc-500">{phone}</p>
                          )}
                          {provider.provider_service_ref_id && (
                            <span className="text-xs font-medium text-emerald-600">
                              From directory
                            </span>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setEditingProviderId(provider.id)}
                            className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteSaved(provider.id)}
                            disabled={isDeleting}
                            className="rounded p-1 text-red-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                type="button"
                onClick={handleAddEmpty}
                className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
              >
                <Plus className="h-3 w-3" />
                Add another provider
              </button>
            </div>
          )}
        </FormGroup>
      </FormSection>

      <button
        type="submit"
        disabled={isDeleting || hasPendingEntries}
        className={
          'inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold ' +
          'bg-blue-600 text-white shadow-sm hover:bg-blue-700 ' +
          'disabled:cursor-not-allowed disabled:opacity-50 ' +
          'transition-colors duration-150'
        }
      >
        {hasPendingEntries
          ? 'Add or remove pending providers first'
          : 'Save & Continue'}
      </button>
    </form>
  );
}

const EditProviderInline = ({
  provider,
  onSave,
  onCancel,
}: EditProviderInlineProps): React.JSX.Element => {
  const existingAddress = getDisplayAddress(provider);
  const existingPhone = getHandleValue(provider, ContactHandleType.PHONE);
  const existingFax = getHandleValue(provider, ContactHandleType.FAX);

  const [treatmentStatus, setTreatmentStatus] = useState<string>(
    provider.treatment_status || '',
  );
  const [careLevel, setCareLevel] = useState<string>(provider.care_level || '');
  const [address, setAddress] = useState(existingAddress);
  const [phone, setPhone] = useState(existingPhone);
  const [fax, setFax] = useState(existingFax);
  const [isSaving, setIsSaving] = useState(false);

  const inputClass =
    'w-full rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500';
  const labelClass = 'text-xs font-medium text-zinc-600';

  async function handleSave(): Promise<void> {
    setIsSaving(true);

    const patch: Partial<MedicalProvider> = {
      treatment_status:
        (treatmentStatus as ProviderTreatmentStatus) || undefined,
      care_level: (careLevel as TreatmentCareLevel) || undefined,
      address: address || undefined,
    };

    const handles: { handle_type: string; value: string; id?: string }[] = [];
    const existingPhoneHandle = provider.handles?.find(
      (handle) => handle.handle_type === ContactHandleType.PHONE,
    );
    const existingFaxHandle = provider.handles?.find(
      (handle) => handle.handle_type === ContactHandleType.FAX,
    );

    if (phone) {
      handles.push({
        handle_type: 'PHONE',
        value: phone,
        ...(existingPhoneHandle?.id ? { id: existingPhoneHandle.id } : {}),
      });
    }

    if (fax) {
      handles.push({
        handle_type: 'FAX',
        value: fax,
        ...(existingFaxHandle?.id ? { id: existingFaxHandle.id } : {}),
      });
    }

    if (handles.length > 0) {
      (patch as Record<string, unknown>).handles = handles;
    }

    try {
      await onSave(patch);
    } finally {
      setIsSaving(false);
    }
  }

  const addressInputId = `provider-address-${provider.id}`;
  const phoneInputId = `provider-phone-${provider.id}`;
  const faxInputId = `provider-fax-${provider.id}`;
  const treatmentStatusInputId = `provider-treatment-status-${provider.id}`;
  const careLevelInputId = `provider-care-level-${provider.id}`;

  return (
    <div className="overflow-hidden rounded-xl border border-blue-200 bg-blue-50/30">
      <div className="flex items-center justify-between border-b border-blue-100 bg-blue-50 px-4 py-2.5">
        <span className="text-xs font-semibold text-zinc-600">
          Edit: {provider.name}
        </span>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs font-medium text-zinc-500 hover:text-zinc-700"
        >
          Cancel
        </button>
      </div>

      <div className="space-y-3 px-4 py-3">
        <div>
          <div className={labelClass}>Address</div>
          <input
            id={addressInputId}
            type="text"
            aria-label="Address"
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            placeholder="Street address, City, State ZIP"
            className={`mt-1 ${inputClass}`}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className={labelClass}>Phone</div>
            <input
              id={phoneInputId}
              type="text"
              aria-label="Phone"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              placeholder="(555) 123-4567"
              className={`mt-1 ${inputClass}`}
            />
          </div>
          <div>
            <div className={labelClass}>Fax</div>
            <input
              id={faxInputId}
              type="text"
              aria-label="Fax"
              value={fax}
              onChange={(event) => setFax(event.target.value)}
              placeholder="(555) 123-4567"
              className={`mt-1 ${inputClass}`}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className={labelClass}>Treatment Status</div>
            <select
              id={treatmentStatusInputId}
              aria-label="Treatment Status"
              value={treatmentStatus}
              onChange={(event) => setTreatmentStatus(event.target.value)}
              className={`mt-1 ${inputClass}`}
            >
              <option value="" disabled>
                Select
              </option>
              {Object.values(ProviderTreatmentStatus)
                .filter((status) => status !== ProviderTreatmentStatus.NEVER_TREATED)
                .map((status) => (
                  <option key={status} value={status}>
                    {getSelectLabel(status)}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <div className={labelClass}>Care Level</div>
            <select
              id={careLevelInputId}
              aria-label="Care Level"
              value={careLevel}
              onChange={(event) => setCareLevel(event.target.value)}
              className={`mt-1 ${inputClass}`}
            >
              <option value="" disabled>
                Select
              </option>
              {Object.values(TreatmentCareLevel).map((careLevelOption) => (
                <option key={careLevelOption} value={careLevelOption}>
                  {getSelectLabel(careLevelOption)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className={
            'w-full rounded-lg px-3 py-2 text-sm font-semibold ' +
            'bg-blue-600 text-white hover:bg-blue-700 ' +
            'disabled:cursor-not-allowed disabled:opacity-50 ' +
            'transition-colors duration-150'
          }
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
